import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, Sparkles, Zap, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { openRazorpayCheckout } from "@/lib/razorpay";
import { trackEvent } from "@/lib/firebase";
import { toast } from "sonner";

export const EnrollCourseButton = ({ courseId, courseName, priceINR, label = "Enroll Now", className = "", "data-testid": testId }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [processing, setProcessing] = useState(false);
  const [successOpen, setSuccessOpen] = useState(false);

  const handleClick = async () => {
    if (!user) {
      toast.info("Please log in to enroll");
      navigate("/login");
      return;
    }
    setProcessing(true);
    try {
      const { data } = await api.post("/payments/order", {
        amount: priceINR * 100, currency: "INR", course: courseName,
      });
      const finish = async (payment) => {
        await api.post("/payments/verify", { order_id: data.order_id, ...payment });
        const enroll = await api.post("/courses/enroll", { course_id: courseId, order_id: data.order_id });
        trackEvent("purchase", { currency: "INR", value: priceINR, items: [{ item_id: courseId, item_name: courseName }] });
        if (enroll.data.already_enrolled) {
          toast.info("You already own this course. Redirecting…");
        } else {
          setSuccessOpen(true);
        }
        setTimeout(() => navigate(`/courses/${courseId}`), enroll.data.already_enrolled ? 400 : 1600);
      };
      if (data.mode === "live") {
        await openRazorpayCheckout({
          keyId: data.key_id,
          amount: data.amount,
          currency: data.currency,
          orderId: data.order_id,
          name: "Language Nest India",
          description: courseName,
          prefill: { name: user?.name || "", email: user?.email || "" },
          onSuccess: async (res) => {
            try {
              await finish({ payment_id: res.razorpay_payment_id, signature: res.razorpay_signature });
            } catch (err) {
              toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
            } finally { setProcessing(false); }
          },
          onDismiss: () => { setProcessing(false); toast.info("Payment cancelled"); },
        });
        return;
      }
      await finish({});
      setProcessing(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      setProcessing(false);
    }
  };

  return (
    <>
      <Button
        onClick={handleClick}
        disabled={processing}
        data-testid={testId}
        className={className}
      >
        {processing ? <Loader2 className="w-4 h-4 animate-spin" /> : <>{label} <ArrowRight className="w-4 h-4 ml-2" /></>}
      </Button>

      <Dialog open={successOpen} onOpenChange={setSuccessOpen}>
        <DialogContent className="rounded-3xl sm:max-w-md text-center">
          <motion.div
            initial={{ scale: 0.5, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
            transition={{ type: "spring", stiffness: 200 }}
            className="w-20 h-20 rounded-full bg-brand-red text-white grid place-items-center mx-auto"
          >
            <Zap className="w-10 h-10" />
          </motion.div>
          <DialogHeader className="mt-4">
            <DialogTitle className="font-heading font-black text-3xl text-brand-navy">Enrolled!</DialogTitle>
            <DialogDescription className="text-brand-navy/60 mt-2">
              You now have lifetime access to <span className="font-semibold text-brand-navy">{courseName}</span>.
              Loading your video dashboard…
            </DialogDescription>
          </DialogHeader>
          <div className="mt-6 flex items-center justify-center gap-2 text-sm text-brand-navy/50">
            <Sparkles className="w-4 h-4 text-brand-gold" /> Welcome to the nest
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
